package top.wcpe.allincore.ui.runtime.ir.state

import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import kotlin.jvm.JvmStatic

/**
 * M5-c #I5：state 写入运行时类型校验工具。
 *
 * 仅当 DSL 作者在 state defaults 中显式书写了类型标注时才会启用：调用方若查不到字段类型（null）或类型为
 * [IrType.ANY]，应直接放行，以保持对现有未标注资源的向后兼容。
 *
 * Nullable 类型参照 Kotlin 语义：`String?` 允许 null 值，非 null 时检查 inner 类型（必须为 String）。
 * [IrType.NULLABLE_ANY] 为兜底类型，允许 null 且非 null 时不做 inner 类型限制。
 */
object StateTypeGuard {

    /**
     * 校验写入到指定 state 字段的值是否满足静态类型约束；不满足时抛 [DslRuntimeError]。
     *
     * @param line   触发该写入的源码行号（1-based），未知时传 0
     * @param column 触发该写入的源码列号（1-based），未知时传 0
     */
    @JvmStatic
    fun checkAssignable(stateName: String, expectedType: IrType?, value: Any?, line: Int, column: Int) {
        if (expectedType == null || expectedType == IrType.ANY) {
            return
        }
        if (value == null) {
            // 所有 NULLABLE_* 类型均允许 null
            if (!expectedType.isNullable()) {
                throw mismatch(stateName, expectedType, null, line, column)
            }
            return
        }
        if (!matchesType(expectedType, value)) {
            throw mismatch(stateName, expectedType, value, line, column)
        }
    }

    /** 非 null 值的运行时类型是否匹配 expectedType；ANY / NULLABLE_ANY 一律放行，未知类型按不匹配处理。 */
    private fun matchesType(expectedType: IrType, value: Any): Boolean = when (expectedType) {
        IrType.NUMBER, IrType.NULLABLE_NUMBER -> value is Double
        IrType.STRING, IrType.NULLABLE_STRING -> value is String
        IrType.BOOLEAN, IrType.NULLABLE_BOOLEAN -> value is Boolean
        IrType.MAP, IrType.NULLABLE_MAP -> value is Map<*, *>
        IrType.LIST, IrType.NULLABLE_LIST -> value is List<*>
        IrType.NULLABLE_ANY, IrType.ANY -> true
    }

    private fun mismatch(
        stateName: String,
        expectedType: IrType,
        value: Any?,
        line: Int,
        column: Int,
    ): DslRuntimeError {
        val actualName = if (value == null) "null" else value::class.simpleName ?: "?"
        // 仅在拥有有效行号时附加位置信息，避免对未知位置造成噪声
        val pos = if (line > 0) " [$line:$column]" else ""
        return DslRuntimeError(
            "state '$stateName' 类型不符：期望 ${displayName(expectedType)}，实际 $actualName$pos",
            line,
            column,
        )
    }

    /** 运行时错误文案展示名。 */
    private fun displayName(type: IrType): String = when (type) {
        IrType.NUMBER -> "Number"
        IrType.STRING -> "String"
        IrType.BOOLEAN -> "Boolean"
        IrType.MAP -> "Map"
        IrType.LIST -> "List"
        IrType.NULLABLE_NUMBER -> "Number?"
        IrType.NULLABLE_STRING -> "String?"
        IrType.NULLABLE_BOOLEAN -> "Boolean?"
        IrType.NULLABLE_MAP -> "Map?"
        IrType.NULLABLE_LIST -> "List?"
        IrType.NULLABLE_ANY -> "Nullable"
        else -> "Any"
    }
}
